import { Button, Modal, Group, Stack, Tooltip } from '@mantine/core';
import { IconVideo } from '@tabler/icons-react';
import { useState, useMemo } from 'react';
import { primaryColor } from '../../../../constants/theme';
import { useApp } from '../../../../context/AppContext';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { useNavigate } from 'react-router-dom';
import { BookingPreview } from './BookingPreview';
import { getBookingTopicLabel } from '../../../../utils/bookingTopic';

interface JoinCallButtonProps {
  groupedBookings: BookingResponseModel[];
  fullWidth?: boolean;
  alwaysShow?: boolean;
}

const VIDEO_CALL_PLATFORM = import.meta.env.VITE_VIDEO_CALL_PLATFORM || 'zoom';

export const JoinCallButton: React.FC<JoinCallButtonProps> = ({
  groupedBookings,
  fullWidth,
  alwaysShow,
}) => {
  const booking = groupedBookings[0];
  const finalBooking = groupedBookings[groupedBookings.length - 1];
  const navigate = useNavigate();
  const { token, profile } = useApp();
  const [opened, setOpened] = useState(false);

  const hasPaid = useMemo(() => {
    return booking.rate === 0 || booking.paymentStatus === 'succeeded';
  }, [booking.rate, booking.paymentStatus]);

  const sessionTopicLabel = useMemo(
    () => getBookingTopicLabel(booking),
    [booking]
  );

  const {
    id,
    videoRoomId,
    startIso,
    videoJwt,
    userName,
    url,
    professionalName,
    professionalId,
    userId,
    accepted,
    isCancelled,
    paymentStatus,
    rate,
  } = booking;
  const { endIso, professionalProfilePicture, userProfilePicture } =
    finalBooking;
  const isProfessional = profile?.id === booking.professionalId;

  const joinCall = () => {
    if (VIDEO_CALL_PLATFORM === 'teams') {
      navigate('/teams-video-call', {
        state: {
          videoRoomId,
          token,
          userName,
          startTime: startIso,
          endTime: endIso,
          bookingId: id,
          professionalId,
          userId,
          userFeedbackGiven:
            booking.userFeedbackGiven ?? booking.UserFeedbackGiven ?? false,
          professionalFeedbackGiven:
            booking.professionalFeedbackGiven ??
            booking.ProfessionalFeedbackGiven ??
            false,
          accepted,
          isCancelled,
          paymentStatus,
          rate,
        },
      });
    } else if (VIDEO_CALL_PLATFORM === 'daily') {
      navigate('/daily-video-call', {
        state: {
          videoUrl: url,
          username: isProfessional ? userName : professionalName,
          startTime: startIso,
          endTime: endIso,
          bookingId: id,
          professionalId,
          userId,
          userFeedbackGiven:
            booking.userFeedbackGiven ?? booking.UserFeedbackGiven ?? false,
          professionalFeedbackGiven:
            booking.professionalFeedbackGiven ??
            booking.ProfessionalFeedbackGiven ??
            false,
          accepted,
          isCancelled,
          paymentStatus,
          rate,
          profilePicture: isProfessional
            ? userProfilePicture
            : professionalProfilePicture,
        },
      });
    } else {
      navigate('/video-call', {
        state: {
          sessionTopic: sessionTopicLabel,
          videoJwt,
          username: userName,
          startTime: startIso,
          endTime: endIso,
          bookingId: id,
          professionalId,
          userId,
          userFeedbackGiven:
            booking.userFeedbackGiven ?? booking.UserFeedbackGiven ?? false,
          professionalFeedbackGiven:
            booking.professionalFeedbackGiven ??
            booking.ProfessionalFeedbackGiven ??
            false,
          accepted,
          isCancelled,
          paymentStatus,
          rate,
        },
      });
    }
  };

  const isWithinAllowedTimeRange = useMemo(() => {
    const now = new Date().getTime();
    const bookingStart = new Date(booking.startIso).getTime();
    const bookingEnd = new Date(finalBooking.endIso).getTime();
    const fifteenMinutesBeforeStart = bookingStart - 15 * 60 * 1000;
    const twoHoursAfterEnd = bookingEnd + 2 * 60 * 60 * 1000;

    return now >= fifteenMinutesBeforeStart && now <= twoHoursAfterEnd;
  }, [booking.startIso, finalBooking.endIso]);

  const canJoin = !(!isWithinAllowedTimeRange ||
    booking.isCancelled ||
    booking.rejected ||
    !booking.accepted)



  if (
    !alwaysShow &&
    !canJoin
  ) {
    return null;
  }



  return (
    <>
      <Tooltip
        label="Awaiting Payment"
        disabled={hasPaid}
        position="bottom"
        events={
          hasPaid
            ? { hover: false, focus: false, touch: false }
            : { hover: true, focus: true, touch: true }
        }
      >
        <Button
          radius="md"
          color={primaryColor}
          leftSection={<IconVideo size={16} />}
          onClick={() => setOpened(true)}
          disabled={!hasPaid || !canJoin}
          data-testid={`join-call-button-${booking.id}`}
          fullWidth={fullWidth ?? false}
        >
          Join Call
        </Button>
      </Tooltip>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
      >
        <Stack gap={10}>
          <BookingPreview
            isProfessional={profile?.id === booking.professionalId}
            title="Join call?"
            groupedBookings={groupedBookings}
            bottomText="The video call room will be available 15 minutes before start time and 2 hours after end time."
          />
          <Group gap={10} justify="center">
            <Button
              variant="outline"
              w="48%"
              color={primaryColor}
              onClick={() => setOpened(false)}
            >
              Cancel
            </Button>
            <Button
              w="48%"
              color={primaryColor}
              onClick={joinCall}
              data-testid="join-call-modal-confirm"
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
